'use client';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useHistory } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import api from '../../../domain/api';
import qs from 'qs';
import BreadCrumb from '../../../components/template/BreadCrumb';
import Header from '../../../components/template/Header';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Function to fetch single campaign daily details (using new dedicated endpoint)
const fetchCampaignDailyDetails = async ({ queryKey }) => {
  const [, { customerId, campaignId, ...filters }] = queryKey;

  if (!customerId || !campaignId) {
    throw new Error('Customer ID and Campaign ID are required');
  }

  const requestBody = {
    customerId,
    campaignId,
    dateFilter: filters.dateFilter || 'last_7_days',
    ...(filters.dateFilter === 'custom' && {
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
  };

  console.log(
    'Making API call to:',
    `${api.defaults.baseURL}/google-ads/campaign/detailed`
  );
  console.log('Request body:', requestBody);

  const res = await api.post('/google-ads/campaign/detailed', requestBody);
  return res.data;
};

const CampaignDetailsView = () => {
  const location = useLocation();
  const history = useHistory();

  // Get campaign details from URL params or state
  const queryParams = useMemo(() => {
    return qs.parse(location.search, { ignoreQueryPrefix: true });
  }, [location.search]);

  const [filters, setFilters] = useState({
    dateFilter: queryParams.dateFilter || 'last_7_days',
    startDate: queryParams.startDate || '',
    endDate: queryParams.endDate || '',
  });

  const [customDateInputs, setCustomDateInputs] = useState({
    startDate: queryParams.startDate || '',
    endDate: queryParams.endDate || '',
  });

  const [activeTab, setActiveTab] = useState('overview');

  // Campaign info from URL params
  const campaignInfo = {
    customerId: queryParams.customerId,
    campaignId: queryParams.campaignId,
    campaignName: queryParams.campaignName,
    customerName: queryParams.customerName,
  };

  // Fetch campaign daily details
  const { data, isLoading } = useQuery({
    queryKey: [
      'google-ads-campaign-daily-details',
      {
        customerId: campaignInfo.customerId,
        campaignId: campaignInfo.campaignId,
        dateFilter: filters.dateFilter,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    ],
    queryFn: fetchCampaignDailyDetails,
    enabled: !!(campaignInfo.customerId && campaignInfo.campaignId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const updateURL = useCallback(() => {
    const query = qs.stringify(
      {
        ...queryParams,
        ...filters,
      },
      { addQueryPrefix: true }
    );
    history.replace({ search: query });
  }, [filters, queryParams, history]);

  useEffect(() => {
    updateURL();
  }, [updateURL]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleCustomDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateInputs((prev) => ({ ...prev, [name]: value }));
  };

  const applyCustomDateFilters = () => {
    setFilters((f) => ({
      ...f,
      startDate: customDateInputs.startDate,
      endDate: customDateInputs.endDate,
    }));
  };

  const handleBackToCampaigns = () => {
    history.push(
      `/adsreports/google-ads?customerId=${campaignInfo.customerId}`
    );
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data?.dailyData) return null;

    const dates = data.dailyData.map((item) =>
      new Date(item.date).toLocaleDateString()
    );
    const impressions = data.dailyData.map((item) => item.impressions || 0);
    const clicks = data.dailyData.map((item) => item.clicks || 0);
    const costs = data.dailyData.map((item) => parseFloat(item.cost || 0));
    const conversions = data.dailyData.map((item) => item.conversions || 0);

    return {
      dates,
      impressions,
      clicks,
      costs,
      conversions,
    };
  }, [data]);

  const performanceChartData = {
    labels: chartData?.dates || [],
    datasets: [
      {
        label: 'Impressions',
        data: chartData?.impressions || [],
        borderColor: '#4285f4',
        backgroundColor: 'rgba(66, 133, 244, 0.1)',
        yAxisID: 'y',
      },
      {
        label: 'Clicks',
        data: chartData?.clicks || [],
        borderColor: '#34a853',
        backgroundColor: 'rgba(52, 168, 83, 0.1)',
        yAxisID: 'y1',
      },
    ],
  };

  const costChartData = {
    labels: chartData?.dates || [],
    datasets: [
      {
        label: 'Daily Cost',
        data: chartData?.costs || [],
        backgroundColor: 'rgba(234, 67, 53, 0.8)',
        borderColor: '#ea4335',
        borderWidth: 1,
      },
    ],
  };

  const conversionChartData = {
    labels: chartData?.dates || [],
    datasets: [
      {
        label: 'Conversions',
        data: chartData?.conversions || [],
        borderColor: '#fbbc04',
        backgroundColor: 'rgba(251, 188, 4, 0.1)',
        fill: true,
      },
    ],
  };

  return (
    <div className='pace-done'>
      <div>
        <Header />
        <BreadCrumb
          title={`Campaign: ${campaignInfo.campaignName || 'Loading...'}`}
          mainLinkTitle='Ads Reports'
          mainLinkUrl='/adsreports'
          activeLink='Campaign Details'
        />

        <div className='container-fluid'>
          <div className='row'>
            <div className='col-12'>
              {/* Back Button */}
              <div className='mb-3'>
                <button
                  className='btn btn-outline-primary btn-sm'
                  onClick={handleBackToCampaigns}
                  title='Back to Campaigns'
                >
                  <i className='fa fa-arrow-left mr-2'></i>
                  Back to Campaigns
                </button>
              </div>

              <div className='campaign-details-container'>
                <style jsx>{`
                  .campaign-details-container {
                    background: #f5f6f7;
                    min-height: auto;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI',
                      Roboto, Helvetica, Arial, sans-serif;
                  }

                  .campaign-header {
                    background: linear-gradient(
                      135deg,
                      #4285f4 0%,
                      #1a73e8 50%,
                      #3367d6 100%
                    );
                    color: white;
                    padding: 12px 24px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    box-shadow: 0 4px 16px rgba(66, 133, 244, 0.2);
                    border-bottom: 2px solid #3367d6;
                    position: relative;
                    overflow: hidden;
                    border-radius: 8px 8px 0 0;
                  }

                  .campaign-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(
                      90deg,
                      transparent,
                      rgba(255, 255, 255, 0.1),
                      transparent
                    );
                    animation: shimmer 3s infinite;
                  }

                  @keyframes shimmer {
                    0% {
                      left: -100%;
                    }
                    100% {
                      left: 100%;
                    }
                  }

                  .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                  }

                  .campaign-info {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    position: relative;
                    z-index: 1;
                  }

                  .campaign-title {
                    font-size: 20px;
                    font-weight: 700;
                    margin: 0;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    letter-spacing: 0.3px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                  }

                  .campaign-title i {
                    font-size: 18px;
                    opacity: 0.95;
                    flex-shrink: 0;
                  }

                  .title-content {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                  }

                  .title-text {
                    font-weight: 700;
                    color: white;
                    white-space: nowrap;
                  }

                  .campaign-subtitle {
                    font-size: 11px;
                    opacity: 0.75;
                    font-weight: 400;
                    font-style: italic;
                    color: rgba(255, 255, 255, 0.8);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  }

                  .date-filter-header {
                    min-width: 200px;
                    position: relative;
                    z-index: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                  }

                  .filter-label {
                    font-size: 12px;
                    font-weight: 600;
                    opacity: 0.9;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 2px;
                  }

                  .filter-select {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    border-radius: 4px;
                    padding: 6px 12px;
                    font-size: 13px;
                    min-width: 160px;
                    backdrop-filter: blur(10px);
                  }

                  .filter-select option {
                    background: #1a73e8;
                    color: white;
                  }

                  .custom-date-inputs {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                    margin-top: 8px;
                  }

                  .date-input {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    border-radius: 4px;
                    padding: 6px 8px;
                    font-size: 12px;
                    width: 130px;
                  }

                  .apply-btn {
                    background: rgba(255, 255, 255, 0.2);
                    border: 1px solid rgba(255, 255, 255, 0.4);
                    color: white;
                    border-radius: 4px;
                    padding: 6px 12px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                  }

                  .apply-btn:hover:not(:disabled) {
                    background: rgba(255, 255, 255, 0.3);
                  }

                  .apply-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                  }

                  .campaign-content {
                    background: white;
                    margin: 0;
                    border-radius: 0 0 8px 8px;
                  }

                  .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 80px 20px;
                    color: #65676b;
                  }

                  .loading-spinner {
                    width: 32px;
                    height: 32px;
                    border: 3px solid #f0f2f5;
                    border-top: 3px solid #4285f4;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 16px;
                  }

                  @keyframes spin {
                    0% {
                      transform: rotate(0deg);
                    }
                    100% {
                      transform: rotate(360deg);
                    }
                  }

                  .summary-section {
                    padding: 24px;
                    background: linear-gradient(
                      135deg,
                      #f8f9fa 0%,
                      #ffffff 100%
                    );
                    border-bottom: 1px solid #e4e6ea;
                  }

                  .summary-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;
                    margin-bottom: 24px;
                  }

                  .summary-card {
                    background: white;
                    border: 1px solid #e4e6ea;
                    border-radius: 12px;
                    padding: 20px;
                    text-align: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                  }

                  .summary-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
                  }

                  .summary-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 12px;
                    font-size: 20px;
                    color: white;
                  }

                  .summary-icon.impressions {
                    background: linear-gradient(135deg, #4285f4, #1a73e8);
                  }

                  .summary-icon.clicks {
                    background: linear-gradient(135deg, #34a853, #137333);
                  }

                  .summary-icon.cost {
                    background: linear-gradient(135deg, #ea4335, #d33b2c);
                  }

                  .summary-icon.conversions {
                    background: linear-gradient(135deg, #fbbc04, #f9ab00);
                  }

                  .summary-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #65676b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 8px;
                  }

                  .summary-value {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1c1e21;
                    margin-bottom: 4px;
                  }

                  .summary-change {
                    font-size: 12px;
                    font-weight: 500;
                  }

                  .summary-change.positive {
                    color: #42b883;
                  }

                  .summary-change.negative {
                    color: #ea4335;
                  }

                  .tabs-container {
                    background: white;
                    border-bottom: 1px solid #e4e6ea;
                    padding: 0 24px;
                  }

                  .tabs {
                    display: flex;
                    gap: 0;
                    overflow-x: auto;
                  }

                  .tab {
                    padding: 16px 24px;
                    border: none;
                    background: none;
                    color: #65676b;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.2s;
                    white-space: nowrap;
                  }

                  .tab:hover {
                    color: #4285f4;
                    background: rgba(66, 133, 244, 0.05);
                  }

                  .tab.active {
                    color: #4285f4;
                    border-bottom-color: #4285f4;
                    background: rgba(66, 133, 244, 0.05);
                  }

                  .tab-content {
                    padding: 24px;
                    min-height: 400px;
                  }

                  .charts-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
                    gap: 24px;
                    margin-bottom: 24px;
                  }

                  .chart-card {
                    background: white;
                    border: 1px solid #e4e6ea;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                  }

                  .chart-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #1c1e21;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  }

                  .chart-container {
                    height: 300px;
                    position: relative;
                  }

                  .data-table-container {
                    background: white;
                    border: 1px solid #e4e6ea;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                  }

                  .data-table {
                    width: 100%;
                    border-collapse: collapse;
                  }

                  .data-table th {
                    background: #f8f9fa;
                    padding: 12px 16px;
                    text-align: left;
                    font-size: 12px;
                    font-weight: 600;
                    color: #65676b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border-bottom: 2px solid #e4e6ea;
                  }

                  .data-table td {
                    padding: 12px 16px;
                    font-size: 14px;
                    color: #1c1e21;
                    border-bottom: 1px solid #f0f2f5;
                  }

                  .data-table tr:hover {
                    background: #f8f9fa;
                  }

                  .metric-value {
                    font-weight: 500;
                  }

                  .cost-value {
                    font-weight: 600;
                    color: #ea4335;
                  }

                  .positive-change {
                    color: #42b883;
                  }

                  .negative-change {
                    color: #ea4335;
                  }

                  @media (max-width: 768px) {
                    .campaign-header {
                      flex-direction: column;
                      gap: 16px;
                      align-items: stretch;
                      padding: 16px;
                    }

                    .summary-cards {
                      grid-template-columns: repeat(
                        auto-fit,
                        minmax(200px, 1fr)
                      );
                      gap: 16px;
                    }

                    .charts-grid {
                      grid-template-columns: 1fr;
                      gap: 16px;
                    }

                    .tabs {
                      flex-wrap: wrap;
                    }

                    .tab {
                      padding: 12px 16px;
                      font-size: 13px;
                    }
                  }
                `}</style>
                {isLoading ? (
                  <div className='loading-container'>
                    <div className='loading-spinner'></div>
                    <p>Loading campaign details...</p>
                  </div>
                ) : (
                  <div>
                    {/* Campaign Header */}
                    <div className='campaign-header'>
                      <div className='header-left'>
                        <div className='campaign-info'>
                          <h1 className='campaign-title'>
                            <i className='fa fa-bullhorn'></i>
                            <div className='title-content'>
                              <span className='title-text'>
                                {campaignInfo.campaignName ||
                                  'Campaign Details'}
                              </span>
                              <span className='campaign-subtitle'>
                                <i className='fa fa-building'></i>
                                <span>{campaignInfo.customerName}</span>
                                <span>•</span>
                                <span>ID: {campaignInfo.campaignId}</span>
                                {data?.campaign?.status && (
                                  <>
                                    <span>•</span>
                                    <span
                                      className={`status-${data.campaign.status.toLowerCase()}`}
                                    >
                                      {data.campaign.status}
                                    </span>
                                  </>
                                )}
                              </span>
                            </div>
                          </h1>
                        </div>
                      </div>
                      <div className='date-filter-header'>
                        <div className='filter-label'>Campaign Analytics</div>
                        <select
                          name='dateFilter'
                          value={filters.dateFilter}
                          onChange={handleFilterChange}
                          className='filter-select'
                        >
                          <option value='yesterday'>Yesterday</option>
                          <option value='last_7_days'>Last 7 Days</option>
                          <option value='last_15_days'>Last 15 Days</option>
                          <option value='last_30_days'>Last 30 Days</option>
                          <option value='custom'>Custom Range</option>
                        </select>
                        {filters.dateFilter === 'custom' && (
                          <div className='custom-date-inputs'>
                            <input
                              name='startDate'
                              type='date'
                              value={customDateInputs.startDate}
                              onChange={handleCustomDateChange}
                              className='date-input'
                            />
                            <input
                              name='endDate'
                              type='date'
                              value={customDateInputs.endDate}
                              onChange={handleCustomDateChange}
                              className='date-input'
                            />
                            <button
                              className='apply-btn'
                              onClick={applyCustomDateFilters}
                              disabled={
                                !customDateInputs.startDate ||
                                !customDateInputs.endDate
                              }
                            >
                              Apply
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='campaign-content'>
                      <div className='summary-cards'>
                        <div className='summary-card'>
                          <div className='summary-icon impressions'>
                            <i className='fa fa-eye'></i>
                          </div>
                          <div className='summary-label'>Total Impressions</div>
                          <div className='summary-value'>
                            {(
                              data?.summary?.totalImpressions || 0
                            ).toLocaleString()}
                          </div>
                          <div className='summary-change positive'>
                            Avg:{' '}
                            {parseInt(
                              data?.summary?.averageDailyImpressions || '0'
                            ).toLocaleString()}
                            /day
                          </div>
                        </div>

                        <div className='summary-card'>
                          <div className='summary-icon clicks'>
                            <i className='fa fa-mouse-pointer'></i>
                          </div>
                          <div className='summary-label'>Total Clicks</div>
                          <div className='summary-value'>
                            {(data?.summary?.totalClicks || 0).toLocaleString()}
                          </div>
                          <div className='summary-change positive'>
                            CTR: {data?.summary?.averageCtr || '0.00'}%
                          </div>
                        </div>

                        <div className='summary-card'>
                          <div className='summary-icon cost'>
                            <i className='fa fa-dollar-sign'></i>
                          </div>
                          <div className='summary-label'>Total Cost</div>
                          <div className='summary-value'>
                            ₹{(data?.summary?.totalCost || 0).toFixed(2)}
                          </div>
                          <div className='summary-change negative'>
                            Avg: ₹
                            {parseFloat(
                              data?.summary?.averageDailyCost || '0'
                            ).toFixed(2)}
                            /day
                          </div>
                        </div>

                        <div className='summary-card'>
                          <div className='summary-icon conversions'>
                            <i className='fa fa-trophy'></i>
                          </div>
                          <div className='summary-label'>Total Conversions</div>
                          <div className='summary-value'>
                            {data?.summary?.totalConversions || 0}
                          </div>
                          <div className='summary-change positive'>
                            Rate:{' '}
                            {data?.summary?.averageConversionRate || '0.00'}%
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className='tabs-container'>
                      <div className='tabs'>
                        <button
                          className={`tab ${
                            activeTab === 'overview' ? 'active' : ''
                          }`}
                          onClick={() => setActiveTab('overview')}
                        >
                          <i className='fa fa-chart-line'></i> Overview
                        </button>
                        <button
                          className={`tab ${
                            activeTab === 'performance' ? 'active' : ''
                          }`}
                          onClick={() => setActiveTab('performance')}
                        >
                          <i className='fa fa-chart-area'></i> Performance
                        </button>
                        <button
                          className={`tab ${
                            activeTab === 'costs' ? 'active' : ''
                          }`}
                          onClick={() => setActiveTab('costs')}
                        >
                          <i className='fa fa-chart-bar'></i> Costs
                        </button>
                        <button
                          className={`tab ${
                            activeTab === 'data' ? 'active' : ''
                          }`}
                          onClick={() => setActiveTab('data')}
                        >
                          <i className='fa fa-table'></i> Daily Data
                        </button>
                      </div>
                    </div>

                    {/* Tab Content */}
                    <div className='tab-content'>
                      {activeTab === 'overview' && (
                        <div className='charts-grid'>
                          <div className='chart-card'>
                            <div className='chart-title'>
                              <i className='fa fa-chart-line'></i>
                              Performance Trends
                            </div>
                            <div className='chart-container'>
                              <Line
                                data={performanceChartData}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    y: {
                                      type: 'linear',
                                      display: true,
                                      position: 'left',
                                      title: {
                                        display: true,
                                        text: 'Impressions',
                                      },
                                    },
                                    y1: {
                                      type: 'linear',
                                      display: true,
                                      position: 'right',
                                      title: { display: true, text: 'Clicks' },
                                      grid: { drawOnChartArea: false },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>

                          <div className='chart-card'>
                            <div className='chart-title'>
                              <i className='fa fa-chart-area'></i>
                              Conversions Over Time
                            </div>
                            <div className='chart-container'>
                              <Line
                                data={conversionChartData}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'performance' && (
                        <div className='charts-grid'>
                          <div className='chart-card'>
                            <div className='chart-title'>
                              <i className='fa fa-chart-line'></i>
                              Impressions & Clicks
                            </div>
                            <div className='chart-container'>
                              <Line
                                data={performanceChartData}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  scales: {
                                    y: {
                                      type: 'linear',
                                      display: true,
                                      position: 'left',
                                      title: {
                                        display: true,
                                        text: 'Impressions',
                                      },
                                    },
                                    y1: {
                                      type: 'linear',
                                      display: true,
                                      position: 'right',
                                      title: { display: true, text: 'Clicks' },
                                      grid: { drawOnChartArea: false },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'costs' && (
                        <div className='charts-grid'>
                          <div className='chart-card'>
                            <div className='chart-title'>
                              <i className='fa fa-chart-bar'></i>
                              Daily Costs
                            </div>
                            <div className='chart-container'>
                              <Bar
                                data={costChartData}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    tooltip: {
                                      callbacks: {
                                        label: function (context) {
                                          return `${
                                            context.dataset.label
                                          }: ₹${context.parsed.y.toFixed(2)}`;
                                        },
                                      },
                                    },
                                  },
                                  scales: {
                                    y: {
                                      ticks: {
                                        callback: function (value) {
                                          return '₹' + value.toFixed(2);
                                        },
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'data' && (
                        <div className='data-table-container'>
                          <table className='data-table'>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Impressions</th>
                                <th>Clicks</th>
                                <th>CTR (%)</th>
                                <th>Cost</th>
                                <th>CPC</th>
                                <th>Conversions</th>
                                <th>Conv. Rate (%)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data?.dailyData?.map((row, index) => (
                                <tr key={index}>
                                  <td>
                                    {new Date(row.date).toLocaleDateString()}
                                  </td>
                                  <td className='metric-value'>
                                    {row.impressions
                                      ? parseInt(
                                          row.impressions
                                        ).toLocaleString()
                                      : '—'}
                                  </td>
                                  <td className='metric-value'>
                                    {row.clicks
                                      ? parseInt(row.clicks).toLocaleString()
                                      : '—'}
                                  </td>
                                  <td className='metric-value'>
                                    {row.ctr
                                      ? parseFloat(row.ctr).toFixed(2)
                                      : '0.00'}
                                  </td>
                                  <td className='cost-value'>
                                    ₹
                                    {row.cost
                                      ? parseFloat(row.cost).toFixed(2)
                                      : '0.00'}
                                  </td>
                                  <td className='metric-value'>
                                    ₹
                                    {row.cost &&
                                    row.clicks &&
                                    parseInt(row.clicks) > 0
                                      ? (
                                          parseFloat(row.cost) /
                                          parseInt(row.clicks)
                                        ).toFixed(2)
                                      : '0.00'}
                                  </td>
                                  <td className='metric-value'>
                                    {row.conversions || 0}
                                  </td>
                                  <td className='metric-value'>
                                    {row.conversions &&
                                    row.clicks &&
                                    parseInt(row.clicks) > 0
                                      ? (
                                          (parseInt(row.conversions) /
                                            parseInt(row.clicks)) *
                                          100
                                        ).toFixed(2)
                                      : row.conversionRate
                                      ? parseFloat(row.conversionRate).toFixed(
                                          2
                                        )
                                      : '0.00'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetailsView;
